import React, { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { ArrowLeft, FileText, Upload, Download, Copy } from "lucide-react";
import { motion } from "framer-motion";

export default function FibankRJX() {
  // Estados da Validação (Parte 1)
  const [arquivoBruto, setArquivoBruto] = useState(null);
  const [arquivoEditado, setArquivoEditado] = useState(null);
  const [divergencias, setDivergencias] = useState([]);
  const [validacaoCompleta, setValidacaoCompleta] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [arquivoCorrigido, setArquivoCorrigido] = useState(null);
  
  // Estados do Processamento (Parte 2)
  const [csvData, setCsvData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const fileInputBrutoRef = useRef(null);
  const fileInputEditadoRef = useRef(null);
  const fileInputRef = useRef(null);

  // ==================== PARTE 1: VALIDAÇÃO ====================
  
  // Função para validar e comparar arquivos
  const validarArquivos = (contentBruto, contentEditado) => {
    console.log('🔍 Iniciando validação de arquivos Fibank...');
    console.log('📌 Estratégia de comparação: NSU (identificador único) ou Data+DocFederal+Valor+Lançamento');
    
    const linhasBruto = contentBruto.split('\n').filter(line => line.trim());
    const linhasEditado = contentEditado.split('\n').filter(line => line.trim());
    
    const divergenciasEncontradas = [];
    let arquivoCorrigidoLinhas = [];

    // Criar mapa do arquivo editado usando chave única (NSU ou data+valor+lancamento)
    const mapaEditado = new Map();
    let contadorNSU = 0;
    let contadorSemNSU = 0;
    
    linhasEditado.forEach((linha, index) => {
      const colunas = linha.split(';');
      
      // Pular cabeçalho e linhas inválidas
      if (index === 0 || colunas.length < 7) {
        return;
      }
      
      const data = colunas[0]?.trim() || '';
      const documentoFederal = colunas[1]?.trim() || '';
      const nome = colunas[2]?.trim() || '';
      const valorStr = colunas[4]?.trim() || '0';
      const lancamento = colunas[5]?.trim() || '';
      const nsu = colunas[8]?.trim() || '';
      
      // Usar NSU como chave única (se disponível), caso contrário data+docFederal+valor+lancamento
      let chave;
      if (nsu && nsu !== '') {
        chave = `NSU:${nsu}`;
        contadorNSU++;
      } else {
        chave = `${data}|${documentoFederal}|${valorStr}|${lancamento}`;
        contadorSemNSU++;
      }
      
      mapaEditado.set(chave, {
        index: index,
        linha: linha,
        colunas: colunas,
        valor: parseFloat(valorStr.replace(/\./g, '').replace(',', '.')) || 0
      });
    });
    
    console.log(`📋 Arquivo EDITADO: ${mapaEditado.size} transações mapeadas (${contadorNSU} com NSU, ${contadorSemNSU} sem NSU)`);

    // Adicionar cabeçalho
    arquivoCorrigidoLinhas.push(linhasEditado[0]);

    // Processar linhas do arquivo bruto
    for (let i = 1; i < linhasBruto.length; i++) {
      const linhaBruto = linhasBruto[i];
      const colunasBruto = linhaBruto.split(';');
      
      // Pular linhas inválidas
      if (colunasBruto.length < 7) {
        continue;
      }

      // Extrair dados do arquivo bruto
      const dataMovimento = colunasBruto[0]?.trim() || '';
      const documentoFederal = colunasBruto[1]?.trim() || '';
      const nome = colunasBruto[2]?.trim() || '';
      const descricaoOperacao = colunasBruto[3]?.trim() || '';
      const valorBrutoStr = colunasBruto[4]?.trim() || '0';
      const valorBruto = parseFloat(valorBrutoStr.replace(/\./g, '').replace(',', '.')) || 0;
      const lancamento = colunasBruto[5]?.trim() || '';
      const saldo = colunasBruto[6]?.trim() || '';
      const nsu = colunasBruto[8]?.trim() || '';
      
      // Criar chave única (mesma lógica do mapa)
      let chave;
      if (nsu && nsu !== '') {
        chave = `NSU:${nsu}`;
      } else {
        chave = `${dataMovimento}|${documentoFederal}|${valorBrutoStr}|${lancamento}`;
      }
      
      // Buscar linha correspondente no arquivo editado
      const registroEditado = mapaEditado.get(chave);
      
      if (!registroEditado) {
        // LINHA AUSENTE NO ARQUIVO EDITADO
        divergenciasEncontradas.push({
          linha: i + 1,
          data: dataMovimento,
          nome: nome,
          descricao: descricaoOperacao,
          valorBruto: valorBruto,
          valorEditado: 0,
          diferenca: -valorBruto,
          tipo: 'AUSENTE_NO_EDITADO'
        });
        
        console.log(`🚫 LINHA AUSENTE NO EDITADO (Linha ${i + 1}): ${nome} | ${descricaoOperacao} | R$ ${valorBruto.toFixed(2)} [Chave: ${chave}]`);
        
        // Adicionar linha completa do bruto ao arquivo corrigido
        arquivoCorrigidoLinhas.push(linhaBruto);
      } else {
        // LINHA ENCONTRADA - Verificar valor
        const valorEditado = registroEditado.valor;
        const diferenca = Math.abs(valorBruto - valorEditado);
        const temDivergenciaValor = diferenca > 0.01;
        
        if (temDivergenciaValor) {
          divergenciasEncontradas.push({
            linha: i + 1,
            data: dataMovimento,
            nome: nome,
            descricao: descricaoOperacao,
            valorBruto: valorBruto,
            valorEditado: valorEditado,
            diferenca: valorEditado - valorBruto,
            tipo: 'VALOR_DIVERGENTE'
          });
          
          console.log(`❌ VALOR DIVERGENTE Linha ${i + 1}: ${nome} | Bruto: R$ ${valorBruto.toFixed(2)} | Editado: R$ ${valorEditado.toFixed(2)}`);
          
          // Corrigir valor
          const colunaCorrigida = [...registroEditado.colunas];
          const valorFormatado = valorBruto.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
          colunaCorrigida[4] = valorFormatado;
          
          arquivoCorrigidoLinhas.push(colunaCorrigida.join(';'));
        } else {
          // Valor correto, manter linha original do editado
          arquivoCorrigidoLinhas.push(registroEditado.linha);
        }
        
        // Marcar como processado
        mapaEditado.delete(chave);
      }
    }

    // Verificar se há linhas no arquivo editado que não existem no bruto
    mapaEditado.forEach((registro, chave) => {
      const colunas = registro.colunas;
      const data = colunas[0]?.trim() || '';
      const nome = colunas[2]?.trim() || '';
      const descricao = colunas[3]?.trim() || '';
      
      divergenciasEncontradas.push({
        linha: registro.index + 1,
        data: data,
        nome: nome,
        descricao: descricao,
        valorBruto: 0,
        valorEditado: registro.valor,
        diferenca: registro.valor,
        tipo: 'AUSENTE_NO_BRUTO'
      });
      
      console.log(`⚠️ LINHA EXTRA NO EDITADO (não existe no bruto): ${nome} | ${descricao} | R$ ${registro.valor.toFixed(2)}`);
    });

    console.log('\n' + '='.repeat(80));
    console.log(`📊 Total de divergências encontradas: ${divergenciasEncontradas.length}`);
    
    if (divergenciasEncontradas.length > 0) {
      const faltantes = divergenciasEncontradas.filter(d => d.tipo === 'AUSENTE_NO_EDITADO').length;
      const extras = divergenciasEncontradas.filter(d => d.tipo === 'AUSENTE_NO_BRUTO').length;
      const divergentes = divergenciasEncontradas.filter(d => d.tipo === 'VALOR_DIVERGENTE').length;
      
      console.log(`   🟣 Linhas faltando no editado: ${faltantes}`);
      console.log(`   🟠 Linhas extras no editado: ${extras}`);
      console.log(`   🔴 Valores divergentes: ${divergentes}`);
    }
    console.log('='.repeat(80));
    
    return {
      divergencias: divergenciasEncontradas,
      conteudoCorrigido: arquivoCorrigidoLinhas.join('\n')
    };
  };

  // Handler para upload do arquivo bruto
  const handleArquivoBruto = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setArquivoBruto({
        nome: file.name,
        conteudo: e.target.result
      });
      console.log('✅ Arquivo bruto carregado:', file.name);
    };
    reader.readAsText(file);
  };

  // Handler para upload do arquivo editado
  const handleArquivoEditado = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setArquivoEditado({
        nome: file.name,
        conteudo: e.target.result
      });
      console.log('✅ Arquivo editado carregado:', file.name);
    };
    reader.readAsText(file);
  };

  // Executar validação
  const executarValidacao = () => {
    if (!arquivoBruto || !arquivoEditado) {
      alert('Por favor, carregue ambos os arquivos (bruto e editado)');
      return;
    }

    setIsValidating(true);
    
    try {
      const resultado = validarArquivos(arquivoBruto.conteudo, arquivoEditado.conteudo);
      
      setDivergencias(resultado.divergencias);
      setArquivoCorrigido(resultado.conteudoCorrigido);
      
      if (resultado.divergencias.length === 0) {
        setValidacaoCompleta(true);
        console.log('✅ Validação concluída: Nenhuma divergência encontrada!');
        console.log('🚀 Processando automaticamente o arquivo validado...');
        
        // Processar automaticamente o arquivo editado validado
        setTimeout(() => {
          setIsProcessing(true);
          try {
            const resultadoProcessamento = processCSV(arquivoEditado.conteudo);
            setCsvData(resultadoProcessamento.data);
            setSummary(resultadoProcessamento.summary);
          } catch (error) {
            console.error('Erro ao processar CSV:', error);
            alert('Erro ao processar o arquivo CSV. Verifique o console para detalhes.');
          } finally {
            setIsProcessing(false);
          }
        }, 500);
      } else {
        console.log(`⚠️ Validação concluída: ${resultado.divergencias.length} divergências encontradas`);
      }
    } catch (error) {
      console.error('❌ Erro na validação:', error);
      alert('Erro ao validar arquivos. Verifique o console para detalhes.');
    } finally {
      setIsValidating(false);
    }
  };

  // Baixar arquivo corrigido
  const baixarArquivoCorrigido = () => {
    if (!arquivoCorrigido) return;

    const blob = new Blob([arquivoCorrigido], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = arquivoEditado.nome.replace('.csv', '_CORRIGIDO.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log('💾 Arquivo corrigido baixado!');
  };

  // Resetar validação
  const resetarValidacao = () => {
    setArquivoBruto(null);
    setArquivoEditado(null);
    setDivergencias([]);
    setValidacaoCompleta(false);
    setArquivoCorrigido(null);
  };

  // ==================== PARTE 2: PROCESSAMENTO ====================
  
  // Função para remover acentos e converter para maiúsculo
  const removerAcentosEMaiusculo = (texto) => {
    const mapaAcentos = {
      'á': 'a', 'à': 'a', 'ã': 'a', 'â': 'a', 'ä': 'a',
      'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e',
      'í': 'i', 'ì': 'i', 'î': 'i', 'ï': 'i',
      'ó': 'o', 'ò': 'o', 'õ': 'o', 'ô': 'o', 'ö': 'o',
      'ú': 'u', 'ù': 'u', 'û': 'u', 'ü': 'u',
      'ç': 'c',
      'ñ': 'n',
      'Á': 'A', 'À': 'A', 'Ã': 'A', 'Â': 'A', 'Ä': 'A',
      'É': 'E', 'È': 'E', 'Ê': 'E', 'Ë': 'E',
      'Í': 'I', 'Ì': 'I', 'Î': 'I', 'Ï': 'I',
      'Ó': 'O', 'Ò': 'O', 'Õ': 'O', 'Ô': 'O', 'Ö': 'O',
      'Ú': 'U', 'Ù': 'U', 'Û': 'U', 'Ü': 'U',
      'Ç': 'C',
      'Ñ': 'N'
    };
    
    let resultado = texto;
    Object.keys(mapaAcentos).forEach(char => {
      resultado = resultado.replace(new RegExp(char, 'g'), mapaAcentos[char]);
    });
    
    resultado = resultado
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    
    resultado = resultado.replace(/[^\x20-\x7E]/g, '');
    
    return resultado.toUpperCase();
  };

  // Função para remover vírgulas
  const removerVirgulas = (texto) => {
    return texto.replace(/,/g, '');
  };

  // Função para processar valor monetário (formato: 3.000,00 ou 20,62)
  const parseValue = (value) => {
    if (!value || value === '---') return 0;
    const cleanValue = value
      .replace(/\s/g, '')
      .replace(/\./g, '')
      .replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  // Função para formatar data no formato DDMMAAAA
  const formatarDataCSV = (dataOriginal) => {
    const match = dataOriginal.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (match) {
      return `${match[1]}${match[2]}${match[3]}`;
    }
    return '01012025';
  };

  // Função para extrair competência (MMAAAA)
  const extrairCompetencia = () => {
    if (!csvData.length) {
      return '012025';
    }
    
    const primeiraData = csvData[0].data;
    const match = primeiraData.match(/\d{2}\/(\d{2})\/(\d{4})/);
    
    if (match) {
      const mes = match[1];
      const ano = match[2];
      return `${mes}${ano}`;
    }
    
    return '012025';
  };

  // Dicionário de mapeamento: Nome -> Código de Débito
  // Mapeamento extraído do CSV final para nomes com Lancamento = 'D'
  const MAPEAMENTO_NOMES_CODIGO = {
    'ADAILTON DE JESUS SILVA': 4867,
    'ALEXANDRE DA SILVA BARBOSA': 4867,
    'ALEXANDRE GHION': 4867,
    'AMADEU BATISTA DOS SANTOS': 4867,
    'ANA CLAUDIA DOS SANTOS DA CRUZ': 4867,
    'ANDERSON LOPES DE JESUS': 4867,
    'ANDRE HELENO DA SILVA': 4867,
    'ANTHONY VALERIANO PAIVA': 4328,
    'CAMILA MARTINS PEREIRA': 4867,
    'CAMILLA STEPHANIE GNAN GONCALVES SAMPAIO': 4867,
    'CHRISPIM MENDES FILHO': 4867,
    'CLAUDIO DE OLIVEIRA MARTINS': 4867,
    'CLAUDIO JOSE MARIANO': 4867,
    'CLAYTON SANTOS NOCETTI': 4867,
    'DAVID ROSARIO': 4867,
    'DEIVID LUIS NOGUEIRA': 4867,
    'EDUARDO PEDROZO CARDOSO': 4867,
    'EDNAMAURA DOS SANTOS': 4867,
    'FABIANO DE JESUS SANTOS': 4867,
    'FERNANDA REGINA DOS SANTOS SILVA': 4867,
    'GABRIELLY GARCIA DO NASCIMENTO': 4867,
    'GERALDA JOANA PASTOR': 4867,
    'GERMANO LOPES DOS SANTOS JUNIOR': 4867,
    'GILDEMAR BAZAN FRANCISCO': 4867,
    'GILSON MOREIRA DA SILVA': 4867,
    'GIOVANI SANTOS DA SILVA': 4867,
    'GOTO COMUNICACAO UNIFICADA DO BRASIL LTDA': 4186,
    'IASMIM MELQUIEDES DOS SANTOS': 4867,
    'IGOR DA CRUZ': 4867,
    'IGOR RAPHAEL ALVES': 4867,
    'IGOR SOUSA SANTOS': 4867,
    'INGRID ALVES DE SA': 4867,
    'IVAN EVANGELISTA DA SILVA': 4867,
    'JEAN ANTONYOS HALBERSTADT DOS SANTOS': 4867,
    'JEFFERSON DA SILVA GARAJAU': 4867,
    'JENIFFER DOS SANTOS GOMES': 4867,
    'JOAQUIM SIMPLICIO FERREIRA NETTO': 4867,
    'JONH REIS ARAUJO': 4867,
    'JORGE HENRIQUE GOIS NAPOLEAO': 4867,
    'JOSE ANTONIO DOS SANTOS': 4867,
    'JOSE DO CARMO ALVES': 4867,
    'JOSE MANOEL DA SILVA': 4867,
    'KARINA FERNANDES DIOGO': 4867,
    'KEVIN ALVES NUNES': 4867,
    'LEANDRO ALMEIDA LEONCIO': 4867,
    'LILIAN MARTINS C NASCIMENTO': 4867,
    'LILIAN MARTINS DA CONCEICAO NASCIMENTO': 4867,
    'LUCINEIA APARECIDA DOS SANTOS OLIVEIRA': 4867,
    'LUIZ CARLOS DO CARMO': 4867,
    'MARCO ANTONIO FERRO GALVAO DE CAMARGO': 4867,
    'MARCOS ANTONIO LOMBARDI JUNIOR': 4867,
    'MARCOS CEZAR MARTINS DOS SANTO': 4867,
    'MARCOS JUNIOR': 4867,
    'MARCOS ROBERTO PALMEIRA': 4867,
    'MARCOS VINICIUS DOS SANTOS': 4867,
    'MARIA EDUARDA DE OLIVEIRA SOUZA': 4867,
    'MARIA REJANE PEREIRA ANDRE': 4867,
    'MARINALDA DE SOUSA BORGES SILVA': 4867,
    'MATHEUS COSTA': 4867,
    'MATHEUS COSTA E SILVA': 4867,
    'MAXIMO DIGITAL': 4866,
    'NATALIA DOS SANTOS SOUSA': 4867,
    'NELSON DA SILVA': 4867,
    'NOE NEGRAO DA SILVA': 4867,
    'ODAIR DAS DORES': 4867,
    'OSEAS AURELIANO DA SILVA': 4867,
    'OSMIR SANTOS SILVA': 4867,
    'OTAVIO SALLES ZANELATO': 4867,
    'PAROQUIA N SRA PERPETUO SOCORRO E STA ROSALIA': 4248,
    'RENE ANTONIO FRANCELINO': 4867,
    'RINALDO RODRIGUES DA SILVA': 4867,
    'ROBSON ROBERTO FAGUNDES': 4032,
    'RODRIGO SOARES AVELINO DA SILVA': 4867,
    'ROGERIO ALMEIDA ZANELATO': 4867,
    'ROGERIO DE ALMEIDA ZANELATO': 4867,
    'SA ASSESSORIA EMPRESARIAL LTDA': 4247,
    'TAMIRES LIMA REIS': 4867,
    'THAINA SOLEDADE DOS SANTOS': 4867,
    'THAINA SOLEDADE DOS SANTOS MALAVAZI': 4867,
    'UFOS TATOO COMERCIO E SERVICOS ESPECIALIZADOS LTDA': 4866,
    'WINDSON MESSIAS ROCHA': 4867
  };

  // Função para processar o CSV
  const processCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const data = [];
    
    let totalRecebimentos = 0;
    let valorTotalRecebimentos = 0;
    let totalPagamentos = 0;
    let valorTotalPagamentos = 0;
    let saldoInicial = 0;
    let totalTransacoes = 0;

    // Pular cabeçalho (linha 0) e saldo anterior (linha 1)
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(';');
      
      // Capturar saldo inicial da linha "SaldoAnterior"
      if (i === 1 && values[3] === 'SaldoAnterior') {
        saldoInicial = parseValue(values[6]); // Coluna Saldo
        console.log(`💰 Saldo Inicial: ${saldoInicial.toFixed(2)}`);
        continue;
      }

      // Ignorar linhas de "Saldo do dia"
      if (values[2] && values[2].toLowerCase().includes('saldo do dia')) {
        continue;
      }

      if (values.length >= 7) {
        const dataMovimento = values[0] || '';
        const documentoFederal = values[1] || '';
        const nome = values[2] || '';
        const descricaoOperacao = values[3] || '';
        const valorMovimento = parseValue(values[4]);
        const lancamento = values[5] || ''; // C ou D
        const saldo = parseValue(values[6]);

        const isRecebimento = lancamento === 'C';
        const isPagamento = lancamento === 'D';
        
        // Identificação das operações - RECEBIMENTOS e PAGAMENTOS
        // Normalizar nome removendo acentos para detecção flexível
        const nomeNormalizado = removerAcentosEMaiusculo(nome);
        const isTovSolucoes = nomeNormalizado.includes('TOV SOLUCOES');
        const isEjxConsultoria = nomeNormalizado.includes('EJX CONSULTORIA');
        
        // Identificação das operações - PAGAMENTOS
        const descricaoUpper = descricaoOperacao.toUpperCase();
        const nomeUpper = nome.toUpperCase();
        
        const isPagamentoComissao = descricaoUpper.includes('PAGAMENTO COMISS') || descricaoUpper.includes('PAGAMENTO COMISSAO');
        const isPagamentoFuncionarioExterno = descricaoUpper.includes('PAGAMENTO FUNCIONARIO EXTERNO');
        const isPagamentoFuncionarioInterno = descricaoUpper.includes('PAGAMENTO FUNCIONARIO INTERNO');
        const isErikaAguiarJesus = nomeUpper.includes('ERIKA AGUIAR DE JESUS');
        const isPagamentoFgts = descricaoUpper.includes('PAGAMENTO FGTS');
        const isEnelDistribuicao = nomeUpper.includes('ENEL DISTRIBUICAO');
        const isSabesp = nomeUpper.includes('SABESP');
        const isRogerioJesus = nomeUpper.includes('ROGERIO DE JESUS');
        const isCustoEnvioPix = descricaoUpper.includes('CUSTO ENVIO PIX');
        const isManutencaoConta = descricaoUpper.includes('MANUTENCAO DE CONTA');
        const isTributosFederais = nomeUpper.includes('TRIBUTOS FEDERAIS');
        const isRjxServicos = nomeUpper.includes('RJX SERVICOS');

        const row = {
          data: dataMovimento,
          documentoFederal,
          nome,
          descricaoOperacao,
          valor: valorMovimento,
          lancamento,
          saldo,
          isRecebimento,
          isPagamento,
          isTovSolucoes,
          isEjxConsultoria,
          isPagamentoComissao,
          isPagamentoFuncionarioExterno,
          isPagamentoFuncionarioInterno,
          isErikaAguiarJesus,
          isPagamentoFgts,
          isEnelDistribuicao,
          isSabesp,
          isRogerioJesus,
          isCustoEnvioPix,
          isManutencaoConta,
          isTributosFederais,
          isRjxServicos
        };

        data.push(row);
        totalTransacoes++;

        if (isRecebimento) {
          totalRecebimentos++;
          valorTotalRecebimentos += valorMovimento;
          console.log(`💚 RECEBIMENTO #${totalRecebimentos}: ${nome} | ${descricaoOperacao} | R$ ${valorMovimento.toFixed(2)}`);
        }

        if (isPagamento) {
          totalPagamentos++;
          valorTotalPagamentos += valorMovimento;
          
          // Log com identificação da regra aplicada
          let regraAplicada = 'PADRÃO (0)';
          if (isRjxServicos) regraAplicada = 'RJX SERVICOS (EXCLUÍDO DO CSV)';
          else if (isPagamentoComissao) regraAplicada = 'COMISSÃO (4032)';
          else if (isPagamentoFuncionarioExterno) regraAplicada = 'FUNC. EXTERNO (4867)';
          else if (isPagamentoFuncionarioInterno) regraAplicada = 'FUNC. INTERNO (4328)';
          else if (isErikaAguiarJesus) regraAplicada = 'ERIKA AGUIAR (4328)';
          else if (isPagamentoFgts) regraAplicada = 'FGTS (4335)';
          else if (isEnelDistribuicao) regraAplicada = 'ENEL (4477)';
          else if (isSabesp) regraAplicada = 'SABESP (4476)';
          else if (isRogerioJesus) regraAplicada = 'ROGERIO (4875)';
          else if (isCustoEnvioPix) regraAplicada = 'CUSTO PIX (4698)';
          else if (isManutencaoConta) regraAplicada = 'MANUTENCAO (4698)';
          
          console.log(`🔴 PAGAMENTO #${totalPagamentos} [${regraAplicada}]: ${nome} | ${descricaoOperacao} | R$ ${valorMovimento.toFixed(2)}`);
        }
      }
    }

    const saldoFinal = saldoInicial + valorTotalRecebimentos - valorTotalPagamentos;

    console.log('='.repeat(80));
    console.log('📊 RESUMO DO PROCESSAMENTO FIBANK');
    console.log('='.repeat(80));
    console.log('💰 Saldo Inicial:', saldoInicial.toFixed(2));
    console.log('📈 Total Recebimentos:', totalRecebimentos, '| Valor:', valorTotalRecebimentos.toFixed(2));
    console.log('📉 Total Pagamentos:', totalPagamentos, '| Valor:', valorTotalPagamentos.toFixed(2));
    console.log('✅ Saldo Final Calculado:', saldoFinal.toFixed(2));
    console.log('='.repeat(80));

    return {
      data,
      summary: {
        totalTransacoes,
        totalRecebimentos,
        valorTotalRecebimentos,
        totalPagamentos,
        valorTotalPagamentos,
        saldoInicial,
        saldoFinal
      }
    };
  };

  // Handler de upload
  const handleFileUpload = (file) => {
    if (!file) return;
    
    setIsProcessing(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const result = processCSV(csvText);
        setCsvData(result.data);
        setSummary(result.summary);
      } catch (error) {
        console.error('Erro ao processar CSV:', error);
        alert('Erro ao processar o arquivo CSV. Verifique o formato do arquivo.');
      } finally {
        setIsProcessing(false);
      }
    };
    
    reader.readAsText(file);
  };

  // Eventos de drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Função para formatar valores monetários
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para gerar CSV formatado para Questor
  const gerarCSVFormatado = () => {
    if (!csvData.length) {
      alert('Por favor, faça upload do arquivo CSV primeiro.');
      return;
    }

    const linhasCSV = [];
    
    // Filtrar transações RJX SERVICOS (serão excluídas do CSV mas incluídas no cálculo)
    const transacoesRjxServicos = csvData.filter(row => row.isRjxServicos);
    const totalValorRjxServicos = transacoesRjxServicos.reduce((sum, row) => sum + row.valor, 0);
    
    console.log('🚫 TRANSAÇÕES RJX SERVICOS (EXCLUÍDAS DO CSV):');
    if (transacoesRjxServicos.length > 0) {
      transacoesRjxServicos.forEach((row, idx) => {
        console.log(`   ${idx + 1}. ${row.data} | ${row.nome} | R$ ${row.valor.toFixed(2)}`);
      });
      console.log(`   📊 Total RJX Serviços: ${transacoesRjxServicos.length} transações | Valor: R$ ${totalValorRjxServicos.toFixed(2)}\n`);
    } else {
      console.log('   Nenhuma transação RJX Serviços encontrada\n');
    }
    
    // Processar RECEBIMENTOS (C) - excluindo RJX SERVICOS
    const recebimentos = csvData.filter(row => row.isRecebimento && !row.isRjxServicos);
    recebimentos.forEach((row, index) => {
      const dataFormatada = formatarDataCSV(row.data);
      let contaDebito = 21;
      let contaCredito = 142; // Padrão para outros recebimentos
      
      // TOV SOLUCOES: débito=21, crédito=8
      if (row.isTovSolucoes) {
        contaCredito = 8;
      }
      // EJX CONSULTORIA: débito=21, crédito=617
      else if (row.isEjxConsultoria) {
        contaCredito = 617;
      }
      // Outros: débito=21, crédito=142 (já definido)
      
      const valor = row.valor.toFixed(2);
      const zero = 0;
      
      const descricaoOriginal = `${row.nome} - ${row.descricaoOperacao}`.trim();
      const descricaoSemAcento = removerAcentosEMaiusculo(descricaoOriginal);
      const descricaoFinal = removerVirgulas(descricaoSemAcento);
      const descricao = `"${descricaoFinal}"`;

      const linha = `${dataFormatada},${contaDebito},${contaCredito},${valor},${zero},${descricao}`;
      linhasCSV.push(linha);
      
      if (index < 5) {
        console.log(`📄 RECEBIMENTO ${index + 1}: Débito=${contaDebito}, Crédito=${contaCredito}, Valor=${valor}`);
      }
    });

    // Processar PAGAMENTOS (D) - excluindo RJX SERVICOS
    const pagamentos = csvData.filter(row => row.isPagamento && !row.isRjxServicos);
    pagamentos.forEach((row, index) => {
      const dataFormatada = formatarDataCSV(row.data);
      let contaDebito = 0; // Padrão para pagamentos não identificados
      const contaCredito = 21; // Sempre 21 para operações D
      
      // Aplicar regras de classificação (ordem de prioridade)
      
      // PRIORIDADE 1: TOV SOLUCOES = código 8
      if (row.isTovSolucoes) {
        contaDebito = 8;
      }
      // PRIORIDADE 2: Verificar se o NOME está no dicionário de mapeamento
      else if (MAPEAMENTO_NOMES_CODIGO[row.nome.trim().toUpperCase().replace(/\s+/g, ' ')]) {
        const nomeNormalizado = row.nome.trim().toUpperCase().replace(/\s+/g, ' ');
        contaDebito = MAPEAMENTO_NOMES_CODIGO[nomeNormalizado];
      }
      // PRIORIDADE 3: Regras baseadas em DescricaoOperacao (se não encontrou no dicionário)
      // PAGAMENTO COMISSAO: débito=4032
      else if (row.isPagamentoComissao) {
        contaDebito = 4032;
      }
      // PAGAMENTO FUNCIONARIO EXTERNO: débito=4867
      else if (row.isPagamentoFuncionarioExterno) {
        contaDebito = 4867;
      }
      // PAGAMENTO FUNCIONARIO INTERNO: débito=4328
      else if (row.isPagamentoFuncionarioInterno) {
        contaDebito = 4328;
      }
      // ERIKA AGUIAR DE JESUS: débito=4328
      else if (row.isErikaAguiarJesus) {
        contaDebito = 4328;
      }
      // PAGAMENTO FGTS: débito=4335
      else if (row.isPagamentoFgts) {
        contaDebito = 4335;
      }
      // ENEL DISTRIBUICAO: débito=4477
      else if (row.isEnelDistribuicao) {
        contaDebito = 4477;
      }
      // SABESP: débito=4476
      else if (row.isSabesp) {
        contaDebito = 4476;
      }
      // ROGERIO DE JESUS: débito=4875
      else if (row.isRogerioJesus) {
        contaDebito = 4875;
      }
      // TRIBUTOS FEDERAIS: débito=4674
      else if (row.isTributosFederais) {
        contaDebito = 4674;
      }
      // CUSTO ENVIO PIX: débito=4698
      else if (row.isCustoEnvioPix) {
        contaDebito = 4698;
      }
      // MANUTENCAO DE CONTA: débito=4698
      else if (row.isManutencaoConta) {
        contaDebito = 4698;
      }
      // Padrão: débito=0, crédito=21 (já definido)
      
      const valor = row.valor.toFixed(2);
      const zero = 0;
      
      const descricaoOriginal = `${row.nome} - ${row.descricaoOperacao}`.trim();
      const descricaoSemAcento = removerAcentosEMaiusculo(descricaoOriginal);
      const descricaoFinal = removerVirgulas(descricaoSemAcento);
      const descricao = `"${descricaoFinal}"`;

      const linha = `${dataFormatada},${contaDebito},${contaCredito},${valor},${zero},${descricao}`;
      linhasCSV.push(linha);
      
      // Log detalhado das regras aplicadas
      if (index < 10 || contaDebito !== 0) {
        let regraLog = '';
        const nomeNormalizado = row.nome.trim().toUpperCase().replace(/\s+/g, ' ');
        
        // Verificar ordem de prioridade conforme aplicado
        if (row.isTovSolucoes) {
          regraLog = ' [TOV SOLUCOES]';
        }
        else if (MAPEAMENTO_NOMES_CODIGO[nomeNormalizado]) {
          regraLog = ' [MAPEAMENTO NOME]';
        }
        else if (row.isPagamentoComissao) regraLog = ' [COMISSÃO]';
        else if (row.isPagamentoFuncionarioExterno) regraLog = ' [FUNC. EXTERNO]';
        else if (row.isPagamentoFuncionarioInterno) regraLog = ' [FUNC. INTERNO]';
        else if (row.isErikaAguiarJesus) regraLog = ' [ERIKA AGUIAR]';
        else if (row.isPagamentoFgts) regraLog = ' [FGTS]';
        else if (row.isEnelDistribuicao) regraLog = ' [ENEL]';
        else if (row.isSabesp) regraLog = ' [SABESP]';
        else if (row.isRogerioJesus) regraLog = ' [ROGERIO]';
        else if (row.isTributosFederais) regraLog = ' [TRIBUTOS FEDERAIS]';
        else if (row.isCustoEnvioPix) regraLog = ' [CUSTO PIX]';
        else if (row.isManutencaoConta) regraLog = ' [MANUTENCAO]';
        else regraLog = ' [PADRÃO]';
        
        console.log(`📄 PAGAMENTO ${index + 1}${regraLog}: Débito=${contaDebito}, Crédito=${contaCredito}, Valor=${valor} | Nome: ${row.nome.substring(0, 40)}`);
      }
    });

    // Contar regras aplicadas (nos pagamentos INCLUÍDOS no CSV)
    const contadores = {
      tovSolucoes: pagamentos.filter(r => r.isTovSolucoes).length,
      mapeamentoNome: pagamentos.filter(r => {
        const nomeNorm = r.nome.trim().toUpperCase().replace(/\s+/g, ' ');
        return !r.isTovSolucoes && MAPEAMENTO_NOMES_CODIGO[nomeNorm];
      }).length,
      comissao: pagamentos.filter(r => r.isPagamentoComissao).length,
      funcExterno: pagamentos.filter(r => r.isPagamentoFuncionarioExterno).length,
      funcInterno: pagamentos.filter(r => r.isPagamentoFuncionarioInterno).length,
      erikaAguiar: pagamentos.filter(r => r.isErikaAguiarJesus).length,
      fgts: pagamentos.filter(r => r.isPagamentoFgts).length,
      enel: pagamentos.filter(r => r.isEnelDistribuicao).length,
      sabesp: pagamentos.filter(r => r.isSabesp).length,
      rogerio: pagamentos.filter(r => r.isRogerioJesus).length,
      tributosFederais: pagamentos.filter(r => r.isTributosFederais).length,
      custoPix: pagamentos.filter(r => r.isCustoEnvioPix).length,
      manutencao: pagamentos.filter(r => r.isManutencaoConta).length,
      padrao: pagamentos.filter(r => {
        const nomeNorm = r.nome.trim().toUpperCase().replace(/\s+/g, ' ');
        return !r.isTovSolucoes && !MAPEAMENTO_NOMES_CODIGO[nomeNorm] &&
               !r.isPagamentoComissao && !r.isPagamentoFuncionarioExterno && 
               !r.isPagamentoFuncionarioInterno && !r.isErikaAguiarJesus && 
               !r.isPagamentoFgts && !r.isEnelDistribuicao && !r.isSabesp && 
               !r.isRogerioJesus && !r.isTributosFederais && !r.isCustoEnvioPix && !r.isManutencaoConta;
      }).length
    };

    const totalRecebimentosCSV = recebimentos.length;
    const totalPagamentosCSV = pagamentos.length;
    const totalExcluidosRJX = transacoesRjxServicos.length;

    console.log(`\n📄 CSV gerado com ${linhasCSV.length} linhas`);
    console.log(`📄 - Recebimentos no CSV: ${totalRecebimentosCSV}`);
    console.log(`📄 - Pagamentos no CSV: ${totalPagamentosCSV}`);
    if (totalExcluidosRJX > 0) {
      console.log(`📄 - Transações RJX Serviços excluídas: ${totalExcluidosRJX} (R$ ${totalValorRjxServicos.toFixed(2)})`);
    }
    console.log('\n📊 RESUMO DAS REGRAS APLICADAS NOS PAGAMENTOS (CSV):');
    if (contadores.tovSolucoes > 0) console.log(`   💰 TOV SOLUCOES (8): ${contadores.tovSolucoes}`);
    if (contadores.mapeamentoNome > 0) console.log(`   📝 Mapeamento por Nome (dicionário): ${contadores.mapeamentoNome}`);
    if (contadores.comissao > 0) console.log(`   💼 Comissão (4032): ${contadores.comissao}`);
    if (contadores.funcExterno > 0) console.log(`   👤 Func. Externo (4867): ${contadores.funcExterno}`);
    if (contadores.funcInterno > 0) console.log(`   👤 Func. Interno (4328): ${contadores.funcInterno}`);
    if (contadores.erikaAguiar > 0) console.log(`   👤 Erika Aguiar (4328): ${contadores.erikaAguiar}`);
    if (contadores.fgts > 0) console.log(`   🏦 FGTS (4335): ${contadores.fgts}`);
    if (contadores.enel > 0) console.log(`   ⚡ Enel (4477): ${contadores.enel}`);
    if (contadores.sabesp > 0) console.log(`   💧 Sabesp (4476): ${contadores.sabesp}`);
    if (contadores.rogerio > 0) console.log(`   👤 Rogerio (4875): ${contadores.rogerio}`);
    if (contadores.tributosFederais > 0) console.log(`   📋 Tributos Federais (4674): ${contadores.tributosFederais}`);
    if (contadores.custoPix > 0) console.log(`   💳 Custo PIX (4698): ${contadores.custoPix}`);
    if (contadores.manutencao > 0) console.log(`   💳 Manutenção (4698): ${contadores.manutencao}`);
    if (contadores.padrao > 0) console.log(`   ❓ Padrão (0): ${contadores.padrao}`);

    // Criar e baixar o arquivo CSV
    const csvContent = linhasCSV.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `FIBANK RJX LANCAMENTOS CSV QUESTOR ${extrairCompetencia()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Função para baixar JSON
  const downloadJSON = () => {
    const dataToDownload = {
      summary,
      transactions: csvData,
      processedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(dataToDownload, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fibank-rjx-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Função para copiar JSON
  const copyJSON = () => {
    const dataToDownload = {
      summary,
      transactions: csvData,
      processedAt: new Date().toISOString()
    };
    
    navigator.clipboard.writeText(JSON.stringify(dataToDownload, null, 2))
      .then(() => alert('JSON copiado para a área de transferência!'))
      .catch(() => alert('Erro ao copiar JSON'));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Cabeçalho */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to={createPageUrl("Operations")}>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Voltar para Operações
              </Button>
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-100 rounded-xl">
              <FileText className="w-8 h-8 text-orange-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">FIBANK - RJX</h1>
              <p className="text-gray-600 mt-1">Gerenciamento de operações contábeis FIBANK</p>
            </div>
          </div>
        </div>

        {/* ==================== PARTE 1: VALIDAÇÃO ==================== */}
        <Card className="mb-8 border-2 border-blue-300 bg-blue-50/30">
          <CardHeader className="bg-blue-100/50">
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <Upload className="w-5 h-5" />
              PARTE 1: Validação de Arquivos
            </CardTitle>
            <p className="text-sm text-blue-700 mt-2">
              Faça upload do arquivo CSV bruto do Fibank e do arquivo CSV editado com as descrições para validar se os valores estão corretos
            </p>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Upload Arquivo Bruto */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                1. Arquivo CSV Bruto (Original do Fibank)
              </label>
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => fileInputBrutoRef.current?.click()}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Selecionar Arquivo Bruto
                </Button>
                {arquivoBruto && (
                  <span className="text-sm text-green-600 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {arquivoBruto.nome}
                  </span>
                )}
              </div>
              <input
                ref={fileInputBrutoRef}
                type="file"
                accept=".csv"
                onChange={(e) => e.target.files[0] && handleArquivoBruto(e.target.files[0])}
                className="hidden"
              />
            </div>

            {/* Upload Arquivo Editado */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                2. Arquivo CSV Editado (Com descrições)
              </label>
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => fileInputEditadoRef.current?.click()}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Selecionar Arquivo Editado
                </Button>
                {arquivoEditado && (
                  <span className="text-sm text-green-600 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {arquivoEditado.nome}
                  </span>
                )}
              </div>
              <input
                ref={fileInputEditadoRef}
                type="file"
                accept=".csv"
                onChange={(e) => e.target.files[0] && handleArquivoEditado(e.target.files[0])}
                className="hidden"
              />
            </div>

            {/* Botão Validar */}
            <div className="flex items-center gap-3">
              <Button
                onClick={executarValidacao}
                disabled={!arquivoBruto || !arquivoEditado || isValidating}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isValidating ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Validando...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Validar Arquivos
                  </>
                )}
              </Button>
              {(arquivoBruto || arquivoEditado) && (
                <Button onClick={resetarValidacao} variant="outline">
                  Resetar
                </Button>
              )}
            </div>

            {/* Validação Completa - Sucesso */}
            {validacaoCompleta && divergencias.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-50 border-2 border-green-300 rounded-lg p-6 mt-6"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-full">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-green-900 mb-2">
                      ✅ Validação Concluída com Sucesso!
                    </h3>
                    <p className="text-green-700">
                      Nenhuma divergência encontrada. O arquivo está validado e será processado automaticamente.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Divergências Encontradas */}
            {divergencias.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border-2 border-red-300 rounded-lg p-6 mt-6"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 bg-red-100 rounded-full">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-red-900 mb-2">
                      ⚠️ {divergencias.length} Divergência(s) Encontrada(s)
                    </h3>
                    <p className="text-red-700 mb-4">
                      Foram encontradas as seguintes divergências entre os arquivos bruto e editado:
                    </p>
                    <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
                      <div className="bg-purple-50 border border-purple-200 rounded p-2">
                        <span className="font-semibold text-purple-900">
                          {divergencias.filter(d => d.tipo === 'AUSENTE_NO_EDITADO').length}
                        </span>
                        <span className="text-purple-700 ml-1">linhas faltando no editado</span>
                      </div>
                      <div className="bg-orange-50 border border-orange-200 rounded p-2">
                        <span className="font-semibold text-orange-900">
                          {divergencias.filter(d => d.tipo === 'AUSENTE_NO_BRUTO').length}
                        </span>
                        <span className="text-orange-700 ml-1">linhas extras no editado</span>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded p-2">
                        <span className="font-semibold text-red-900">
                          {divergencias.filter(d => d.tipo === 'VALOR_DIVERGENTE').length}
                        </span>
                        <span className="text-red-700 ml-1">valores divergentes</span>
                      </div>
                    </div>

                    {/* Tabela de Divergências */}
                    <div className="overflow-x-auto bg-white rounded-lg border border-red-200">
                      <table className="w-full text-sm">
                        <thead className="bg-red-100">
                          <tr>
                            <th className="text-left p-3 font-medium text-red-900">Tipo</th>
                            <th className="text-left p-3 font-medium text-red-900">Linha</th>
                            <th className="text-left p-3 font-medium text-red-900">Data</th>
                            <th className="text-left p-3 font-medium text-red-900">Nome</th>
                            <th className="text-right p-3 font-medium text-red-900">Valor Bruto</th>
                            <th className="text-right p-3 font-medium text-red-900">Valor Editado</th>
                            <th className="text-right p-3 font-medium text-red-900">Diferença</th>
                          </tr>
                        </thead>
                        <tbody>
                          {divergencias.map((div, index) => (
                            <tr key={index} className="border-b border-red-100">
                              <td className="p-3">
                                {div.tipo === 'AUSENTE_NO_EDITADO' && (
                                  <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                                    FALTA NO EDITADO
                                  </span>
                                )}
                                {div.tipo === 'AUSENTE_NO_BRUTO' && (
                                  <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium">
                                    EXTRA NO EDITADO
                                  </span>
                                )}
                                {div.tipo === 'VALOR_DIVERGENTE' && (
                                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                                    VALOR DIVERGENTE
                                  </span>
                                )}
                              </td>
                              <td className="p-3">{div.linha}</td>
                              <td className="p-3">{div.data}</td>
                              <td className="p-3 max-w-xs truncate" title={div.nome}>
                                {div.nome}
                              </td>
                              <td className="p-3 text-right font-medium text-green-700">
                                {div.tipo === 'AUSENTE_NO_BRUTO' ? '-' : `R$ ${div.valorBruto.toFixed(2)}`}
                              </td>
                              <td className="p-3 text-right font-medium text-red-700">
                                {div.tipo === 'AUSENTE_NO_EDITADO' ? '-' : `R$ ${div.valorEditado.toFixed(2)}`}
                              </td>
                              <td className="p-3 text-right font-bold text-red-900">
                                {div.diferenca > 0 ? '+' : ''}R$ {div.diferenca.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Botão para baixar arquivo corrigido */}
                    <div className="mt-4">
                      <Button
                        onClick={baixarArquivoCorrigido}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Baixar Arquivo Editado Corrigido
                      </Button>
                      <p className="text-sm text-red-700 mt-2">
                        <strong>O arquivo corrigido terá:</strong>
                      </p>
                      <ul className="text-sm text-red-700 mt-1 ml-4 list-disc">
                        <li>Valores ajustados para corresponder ao arquivo bruto</li>
                        <li>Linhas faltantes adicionadas do arquivo bruto</li>
                        <li>Linhas extras removidas (se houver)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>

        {/* ==================== PARTE 2: PROCESSAMENTO ==================== */}
        <Card className={`mb-8 border-2 ${validacaoCompleta ? 'border-orange-300 bg-orange-50/30' : 'border-gray-200 bg-gray-50/30 opacity-60'}`}>
          <CardHeader className={validacaoCompleta ? 'bg-orange-100/50' : 'bg-gray-100/50'}>
            <CardTitle className={`flex items-center gap-2 ${validacaoCompleta ? 'text-orange-900' : 'text-gray-500'}`}>
              <FileText className="w-5 h-5" />
              PARTE 2: Processamento para Questor
            </CardTitle>
            <p className={`text-sm mt-2 ${validacaoCompleta ? 'text-orange-700' : 'text-gray-500'}`}>
              {validacaoCompleta 
                ? summary 
                  ? 'Arquivo processado com sucesso! Você pode fazer o upload de outro arquivo se necessário'
                  : 'Processando arquivo validado automaticamente...'
                : 'Complete a validação na Parte 1 antes de processar o arquivo'}
            </p>
          </CardHeader>
          <CardContent>
            {!validacaoCompleta && (
              <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-8 text-center">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="text-gray-600 font-medium">
                  Esta seção estará disponível após a validação dos arquivos
                </p>
              </div>
            )}

            {validacaoCompleta && !summary && (
              <div className="bg-orange-100 border-2 border-orange-300 rounded-lg p-8 text-center">
                <div className="animate-spin w-12 h-12 border-4 border-orange-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-orange-800 font-medium text-lg">
                  Processando arquivo validado...
                </p>
                <p className="text-orange-700 text-sm mt-2">
                  Aguarde enquanto geramos o resumo financeiro
                </p>
              </div>
            )}

            {validacaoCompleta && summary && (
              <>
                <div className="bg-green-100 border-2 border-green-300 rounded-lg p-6 mb-6 text-center">
                  <svg className="w-12 h-12 text-green-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-green-800 font-semibold text-lg">
                    Processamento Concluído!
                  </p>
                  <p className="text-green-700 text-sm mt-1">
                    Arquivo processado: <strong>{arquivoEditado?.nome}</strong>
                  </p>
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <p className="text-sm text-gray-600 text-center mb-3">
                    Deseja processar outro arquivo? (Opcional)
                  </p>
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                      isDragOver 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-gray-300 hover:border-orange-400'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="flex flex-col items-center gap-3">
                      <Upload className="w-6 h-6 text-gray-400" />
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <Upload className="w-4 h-4" />
                        Selecionar Outro Arquivo
                      </Button>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Resumo Financeiro */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-indigo-50 border-indigo-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-indigo-600 text-sm font-medium">Saldo Inicial</p>
                    <p className="text-2xl font-bold text-indigo-700">
                      {formatCurrency(summary.saldoInicial)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-blue-600 text-sm font-medium">Recebimentos</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {formatCurrency(summary.valorTotalRecebimentos)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-red-600 text-sm font-medium">Pagamentos</p>
                    <p className="text-2xl font-bold text-red-700">
                      {formatCurrency(summary.valorTotalPagamentos)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-green-600 text-sm font-medium">Saldo Final</p>
                    <p className="text-2xl font-bold text-green-700">
                      {formatCurrency(summary.saldoFinal)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Card de Transações Excluídas (se houver) */}
            {csvData.some(row => row.isRjxServicos) && (
              <Card className="mb-8 bg-yellow-50 border-2 border-yellow-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-yellow-100 rounded-full">
                      <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-yellow-900 mb-1">
                        Transações RJX Serviços
                      </h3>
                      <p className="text-yellow-800 mb-2">
                        {csvData.filter(row => row.isRjxServicos).length} transação(ões) será(ão) <strong>excluída(s) do CSV</strong> de exportação, 
                        mas incluída(s) no cálculo do saldo acima.
                      </p>
                      <p className="text-yellow-900 font-semibold">
                        Valor total: {formatCurrency(csvData.filter(row => row.isRjxServicos).reduce((sum, row) => sum + row.valor, 0))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Botões de Ação */}
            <div className="flex flex-wrap gap-4 mb-8">
              <Button onClick={downloadJSON} className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Baixar JSON
              </Button>
              <Button variant="outline" onClick={copyJSON} className="flex items-center gap-2">
                <Copy className="w-4 h-4" />
                Copiar JSON
              </Button>
              <Button 
                onClick={gerarCSVFormatado} 
                className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700"
              >
                <Download className="w-4 h-4" />
                Baixar CSV Formatado para Questor
              </Button>
            </div>

            {/* Tabela de Transações */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Transações Processadas
                </CardTitle>
                {csvData.some(row => row.isRjxServicos) && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      ⚠️ <strong>Nota:</strong> Transações com <strong>RJX SERVICOS</strong> são incluídas no cálculo do saldo, 
                      mas <strong>excluídas do CSV final</strong> de exportação para o Questor. 
                      Elas estão destacadas em amarelo na tabela abaixo.
                    </p>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-3 font-medium text-gray-700">Tipo</th>
                        <th className="text-left p-3 font-medium text-gray-700">Data</th>
                        <th className="text-left p-3 font-medium text-gray-700">Nome</th>
                        <th className="text-left p-3 font-medium text-gray-700">Descrição</th>
                        <th className="text-right p-3 font-medium text-gray-700">Valor</th>
                        <th className="text-right p-3 font-medium text-gray-700">Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.map((row, index) => {
                        const descricaoLimpa = removerAcentosEMaiusculo(`${row.nome} - ${row.descricaoOperacao}`.trim());
                        
                        return (
                          <tr key={index} className={`border-b border-gray-200 ${row.isRjxServicos ? 'bg-yellow-50' : ''}`}>
                            <td className="p-3">
                              {row.isRjxServicos ? (
                                <div className="flex flex-col gap-1">
                                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                                    PAGAMENTO
                                  </span>
                                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                                    EXCLUÍDO DO CSV
                                  </span>
                                </div>
                              ) : row.isRecebimento ? (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                                  RECEBIMENTO
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                                  PAGAMENTO
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-gray-700">{row.data}</td>
                            <td className="p-3 text-gray-700">{row.nome}</td>
                            <td className="p-3 text-gray-700 max-w-md truncate">{descricaoLimpa}</td>
                            <td className={`p-3 text-right font-medium ${row.isRecebimento ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(row.valor)}
                            </td>
                            <td className="p-3 text-right text-gray-700">
                              {formatCurrency(row.saldo)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
